import type { DetectorResult } from "../core/types.js";
import type { HeliusTransaction } from "../sources/helius.js";

/**
 * On-chain cadence detector.
 *
 * Autonomous agents transact in bursts when triggered by external events,
 * and show either perfectly regular cron-like activity or reactive bursts.
 * Humans transact sporadically with long gaps and clustered around waking
 * hours.
 *
 * Signals analyzed:
 *  - Transaction time-of-day distribution
 *  - Burst detection (many txs within seconds = bot)
 *  - Program diversity (agents hit few programs, humans hit many)
 *  - Fee behavior (agents pay consistent priority fees)
 */
export function onchainDetector(txs: HeliusTransaction[]): DetectorResult {
  const notes: string[] = [];

  if (txs.length < 10) {
    notes.push(`only ${txs.length} transactions, low confidence`);
    return {
      detector: "onchain",
      score: 50,
      samples: txs.length,
      notes,
      runtime_ms: 0,
    };
  }

  // Time-of-day distribution.
  const hourBuckets = new Array(24).fill(0);
  for (const t of txs) {
    const hour = new Date(t.timestamp * 1000).getUTCHours();
    hourBuckets[hour]++;
  }
  let entropy = 0;
  for (const count of hourBuckets) {
    if (count > 0) {
      const p = count / txs.length;
      entropy -= p * Math.log2(p);
    }
  }
  const entropyScore = (entropy / Math.log2(24)) * 100;
  notes.push(`onchain hour entropy: ${entropy.toFixed(2)} bits`);

  // Burst detection: how many tx clusters have >3 txs within 10 seconds?
  const sorted = [...txs].sort((a, b) => a.timestamp - b.timestamp);
  let bursts = 0;
  for (let i = 0; i < sorted.length - 3; i++) {
    const window = sorted[i + 3]!.timestamp - sorted[i]!.timestamp;
    if (window <= 10) bursts++;
  }
  const burstRatio = bursts / Math.max(1, sorted.length - 3);
  notes.push(`burst clusters: ${bursts} (${(burstRatio * 100).toFixed(1)}% of windows)`);

  // Program diversity: unique program IDs touched.
  const programs = new Set<string>();
  for (const t of txs) {
    for (const p of t.programs ?? []) {
      programs.add(p);
    }
  }
  const diversityScore = Math.min(100, programs.size * 10);
  notes.push(`${programs.size} unique programs touched`);

  // Fee consistency: std dev of priority fees (lower = bot-like).
  const fees = txs.map((t) => t.fee ?? 5000).filter((f) => f > 0);
  const meanFee = fees.reduce((a, b) => a + b, 0) / Math.max(1, fees.length);
  const feeStd = Math.sqrt(
    fees.reduce((a, b) => a + (b - meanFee) ** 2, 0) / Math.max(1, fees.length)
  );
  const feeCV = meanFee > 0 ? feeStd / meanFee : 0;
  notes.push(`fee CV: ${feeCV.toFixed(2)}`);

  // Aggregate.
  let score = 0;
  score += entropyScore * 0.35;
  score += Math.min(100, burstRatio * 300) * 0.3;
  score += (100 - diversityScore) * 0.2; // low diversity = more agent-like
  score += Math.max(0, 100 - feeCV * 50) * 0.15;

  return {
    detector: "onchain",
    score: Math.round(Math.max(0, Math.min(100, score))),
    samples: txs.length,
    notes,
    runtime_ms: 0,
  };
}
